import { useEffect, useState } from "react";
import { api, errMsg, newIdemKey } from "../api";
import { Select } from "./ui";

/** Rateio por percentual entre centros de custo e projetos (somente PJ). Cada dimensão deve fechar 100%. */
export interface AllocItem { refId: string; bp: number }
export interface AllocValue { centro: AllocItem[]; projeto: AllocItem[] }
interface Dim { id: string; name: string; code?: string | null }
type Dimension = "centro" | "projeto";
type OwnerType = "transaction" | "card_charge";

export const pctText = (bp: number) => (bp / 100).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const parsePct = (s: string) => { const n = Number(s.trim().replace(/\./g, "").replace(",", ".").replace("%", "")); return Number.isFinite(n) ? Math.round(n * 100) : 0; };

/** Mensagem de erro se alguma dimensão preenchida não fecha 100% ou repete o item; null se estiver tudo certo (ou nada foi alterado). */
export function allocError(v: AllocValue | null): string | null {
  if (!v) return null;
  for (const [d, label] of [["centro", "centros de custo"], ["projeto", "projetos"]] as const) {
    const items = v[d].filter((i) => i.refId);   // linhas em branco são ignoradas: o rateio é opcional
    if (!items.length) continue;
    if (new Set(items.map((i) => i.refId)).size !== items.length) return `O mesmo item aparece duas vezes no rateio de ${label}.`;
    const sum = items.reduce((s, i) => s + i.bp, 0);
    if (sum !== 10000) return `O rateio de ${label} soma ${pctText(sum)}%; precisa somar 100%.`;
  }
  return null;
}

export async function saveAllocations(realm: string, ownerType: OwnerType, ownerId: string, v: AllocValue) {
  for (const dimension of ["centro", "projeto"] as const) {
    await api("PUT", `/api/${realm}/allocations`, { ownerType, ownerId, dimension, items: v[dimension].filter((i) => i.refId) }, newIdemKey());
  }
}

export function AllocationField({ realm, entityId, ownerType, ownerId, value, setValue }: {
  realm: string; entityId: string; ownerType: OwnerType; ownerId?: string; value: AllocValue | null; setValue: (v: AllocValue | null) => void;
}) {
  const [dims, setDims] = useState<{ centro: Dim[]; projeto: Dim[] } | null>(null);
  const [loaded, setLoaded] = useState<AllocValue>({ centro: [], projeto: [] });
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (realm !== "pj" || !entityId) return;
    let live = true;
    void (async () => {
      try {
        const [c, p] = await Promise.all([api<Dim[]>("GET", `/api/${realm}/cost-centers?entityId=${entityId}`), api<Dim[]>("GET", `/api/${realm}/projects?entityId=${entityId}`)]);
        let cur: AllocValue = { centro: [], projeto: [] };
        if (ownerId) {
          const a = await api<{ centro: { refId: string; bp: number }[]; projeto: { refId: string; bp: number }[] }>("GET", `/api/${realm}/allocations?ownerType=${ownerType}&ownerId=${ownerId}`);
          cur = { centro: a.centro.map((x) => ({ refId: x.refId, bp: x.bp })), projeto: a.projeto.map((x) => ({ refId: x.refId, bp: x.bp })) };
        }
        if (live) { setDims({ centro: c, projeto: p }); setLoaded(cur); if (cur.centro.length || cur.projeto.length) setOpen(true); }
      } catch (e) { if (live) setErr(errMsg(e)); }
    })();
    return () => { live = false; };
  }, [realm, entityId, ownerType, ownerId]);

  if (realm !== "pj") return null;
  if (err) return <p className="small muted">Rateio indisponível: {err}</p>;
  if (!dims) return null;
  if (!dims.centro.length && !dims.projeto.length) return <p className="small muted" data-alloc-empty>Para ratear por centro de custo ou projeto, cadastre-os em Empresa › Centros de custo e projetos.</p>;
  const cur: AllocValue = value ?? loaded;
  const set = (d: Dimension, items: AllocItem[]) => setValue({ ...cur, [d]: items });
  const section = (d: Dimension, title: string, list: Dim[]) => {
    if (!list.length) return null;
    const items = cur[d]; const sum = items.filter((i) => i.refId).reduce((s, i) => s + i.bp, 0);
    return (
      <div className="alloc-sec" data-alloc={d}>
        <div className="alloc-head"><strong>{title}</strong>
          {items.some((i) => i.refId) && <span className={`small ${sum === 10000 ? "pos" : "neg"}`}>soma {pctText(sum)}%</span>}</div>
        {items.map((it, n) => (
          <div className="alloc-row" key={n}>
            <Select value={it.refId} onChange={(e) => set(d, items.map((x, i) => (i === n ? { ...x, refId: e.target.value } : x)))} aria-label={`${title} ${n + 1}`}>
              <option value="">Escolha…</option>
              {list.map((x) => <option key={x.id} value={x.id}>{x.code ? `${x.code} · ` : ""}{x.name}</option>)}
            </Select>
            <input className="input alloc-pct" inputMode="decimal" defaultValue={pctText(it.bp)} key={`${n}-${it.bp}`} aria-label="Percentual"
              onBlur={(e) => {
                const bp = Math.min(10000, Math.max(1, parsePct(e.target.value)));
                // com duas linhas, a outra fica com o que sobra para fechar 100%
                set(d, items.map((x, i) => (i === n ? { ...x, bp } : items.length === 2 && bp < 10000 ? { ...x, bp: 10000 - bp } : x)));
              }} /> <span className="muted">%</span>
            <button type="button" className="link danger" onClick={() => set(d, items.filter((_, i) => i !== n))} aria-label="Remover linha">remover</button>
          </div>))}
        <div className="alloc-actions">
          <button type="button" className="link" data-alloc-add={d} onClick={() => set(d, [...items, { refId: "", bp: items.length ? Math.max(1, 10000 - sum) : 10000 }])}>+ {items.length ? "Dividir com outro" : "Adicionar"}</button>
          {items.length > 1 && <button type="button" className="link" onClick={() => { const n = items.length, base = Math.floor(10000 / n); set(d, items.map((x, i) => ({ ...x, bp: i === n - 1 ? 10000 - base * (n - 1) : base }))); }}>Dividir igualmente</button>}
        </div>
      </div>);
  };
  return (
    <div className="alloc-field">
      <button type="button" className="link" onClick={() => setOpen(!open)} aria-expanded={open} data-alloc-toggle>{open ? "▾" : "▸"} Rateio por centro de custo e projeto <span className="muted small">(opcional)</span></button>
      {open && <>{section("centro", "Centro de custo", dims.centro)}{section("projeto", "Projeto", dims.projeto)}</>}
    </div>
  );
}
